// src/screens/ClubTeams.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Shield,
  Plus,
  UserPlus,
  Trash2,
  ArrowRight,
  Search,
  X,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { supabase } from "../supabaseClient";

/** ===== Types ===== */
type ClubRole =
  | "dirigeant"
  | "cadre_technique"
  | "coach"
  | "joueur"
  | "parent"
  | "lecteur";

type TeamRole = "coach" | "joueur" | "parent";

type ClubMemberRow = {
  id: string;
  club_id: string;
  user_id: string | null;
  email: string | null;
  role: string;
  status: string;
  accepted_at: string | null;
  created_at: string | null;
};

type ClubMember = {
  id: string;
  userId: string | null;
  name: string;
  email: string;
  roles: ClubRole[];
};

type TeamRow = {
  id: string;
  club_id: string;
  team_name: string | null;
  league: string | null;
};

type TeamMemberRow = {
  id: string;
  team_id: string;
  user_id: string | null;
  invited_email: string | null;
  status: string | null;
  role: string;
  accepted_at: string | null;
  player_json_id: string | null;
  player_number: number | null;
};

type Team = {
  id: string;
  name: string;
  category?: string;
  members: {
    coaches: string[]; // club_member.id
    players: string[]; // club_member.id
    parents: string[]; // club_member.id
    parentLinks: Array<{ parentId: string; playerId: string }>;
  };
};

/** ===== Helpers ===== */
function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function uniq<T>(arr: T[]) {
  return Array.from(new Set(arr));
}

function memberRoleToUiRole(dbRole: string): ClubRole | null {
  switch (dbRole) {
    case "dirigeant":
    case "admin":
    case "manager":
      return "dirigeant";
    case "cadre_technique":
    case "assistant":
      return "cadre_technique";
    case "coach":
      return "coach";
    case "joueur":
    case "player":
      return "joueur";
    case "parent":
    case "player_parent":
      return "parent";
    case "lecteur":
    case "viewer":
      return "lecteur";
    default:
      return null;
  }
}

function teamMemberRoleToUiRole(dbRole: string): TeamRole | null {
  switch (dbRole) {
    case "coach":
      return "coach";
    case "joueur":
    case "player":
      return "joueur";
    case "parent":
    case "player_parent":
      return "parent";
    default:
      return null;
  }
}

function getFallbackNameFromEmail(email?: string | null) {
  if (!email) return "Membre";
  const raw = email.split("@")[0] || "membre";
  return raw
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeEmail(email?: string | null) {
  return (email || "").trim().toLowerCase();
}

/** ===== UI small ===== */
function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
      {children}
    </span>
  );
}

export default function ClubTeams() {
  const { clubId } = useParams<{ clubId: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [myRoles, setMyRoles] = useState<ClubRole[]>([]);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);

  const canAccess =
    myRoles.includes("dirigeant") ||
    myRoles.includes("cadre_technique") ||
    myRoles.includes("coach");

  const canManage = canAccess;

  const canCreateTeam =
    myRoles.includes("dirigeant") || myRoles.includes("cadre_technique");

  const [selectedTeamId, setSelectedTeamId] = useState<string>("");

  const selectedTeam = useMemo(
    () => teams.find((t) => t.id === selectedTeamId) ?? null,
    [teams, selectedTeamId]
  );

  const [q, setQ] = useState("");

  const [pickCoach, setPickCoach] = useState<string>("");
  const [pickPlayer, setPickPlayer] = useState<string>("");
  const [pickParent, setPickParent] = useState<string>("");

  const loadPage = useCallback(async () => {
    if (!clubId) {
      setErrorMsg("Club introuvable.");
      setLoading(false);
      return;
    }

    setErrorMsg(null);

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError) throw authError;
    if (!user) throw new Error("Utilisateur non connecté.");

    const [membersRes, teamsRes, teamMembersRes] = await Promise.all([
      supabase
        .from("club_members")
        .select("id, club_id, user_id, email, role, status, accepted_at, created_at")
        .eq("club_id", clubId)
        .eq("status", "active"),
      supabase
        .from("teams")
        .select("id, club_id, team_name, league")
        .eq("club_id", clubId)
        .or("archived.is.null,archived.eq.false")
        .order("team_name", { ascending: true }),
      supabase
        .from("team_members")
        .select(
          "id, team_id, user_id, invited_email, status, role, accepted_at, player_json_id, player_number"
        ),
    ]);

    if (membersRes.error) throw membersRes.error;
    if (teamsRes.error) throw teamsRes.error;
    if (teamMembersRes.error) throw teamMembersRes.error;

    const memberRows = (membersRes.data ?? []) as ClubMemberRow[];
    const teamRows = (teamsRes.data ?? []) as TeamRow[];
    const teamMemberRows = (teamMembersRes.data ?? []) as TeamMemberRow[];

    console.log("AUTH USER", user);
    console.log("CLUB MEMBERS RAW", memberRows);
    console.log("TEAMS RAW", teamRows);
    console.log("TEAM MEMBERS RAW", teamMemberRows);

    const myMemberRows = memberRows.filter(
      (row) =>
        row.user_id === user.id ||
        (row.email &&
          user.email &&
          row.email.toLowerCase() === user.email.toLowerCase())
    );

    const roles = myMemberRows
      .map((row) => memberRoleToUiRole(row.role))
      .filter(Boolean) as ClubRole[];

    console.log("MY MEMBER ROWS", myMemberRows);
    console.log("MY ROLES", roles);

    if (roles.length === 0) {
      throw new Error("Tu n’as pas accès à ce club.");
    }

    const groupedByEmail = new Map<string, ClubMember>();

    for (const row of memberRows) {
      const uiRole = memberRoleToUiRole(row.role);
      if (!uiRole) continue;

      const key = normalizeEmail(row.email) || row.id;
      const existing = groupedByEmail.get(key);

      if (existing) {
        existing.roles = uniq([...existing.roles, uiRole]);
        if (!existing.userId && row.user_id) existing.userId = row.user_id;
      } else {
        groupedByEmail.set(key, {
          id: row.id,
          userId: row.user_id,
          name: getFallbackNameFromEmail(row.email),
          email: row.email || "—",
          roles: [uiRole],
        });
      }
    }

    const clubMembers = Array.from(groupedByEmail.values());

    const clubMemberByUserId = new Map<string, ClubMember>();
    const clubMemberByEmail = new Map<string, ClubMember>();

    for (const m of clubMembers) {
      if (m.userId) clubMemberByUserId.set(m.userId, m);
      if (normalizeEmail(m.email)) clubMemberByEmail.set(normalizeEmail(m.email), m);
    }

    const builtTeams: Team[] = teamRows.map((team) => {
      const rowsForTeam = teamMemberRows.filter((tm) => tm.team_id === team.id);

      const coaches: string[] = [];
      const players: string[] = [];
      const parents: string[] = [];

      for (const tm of rowsForTeam) {
        const uiRole = teamMemberRoleToUiRole(tm.role);
        if (!uiRole) continue;

        const matchedMember =
          (tm.user_id ? clubMemberByUserId.get(tm.user_id) : undefined) ||
          (tm.invited_email ? clubMemberByEmail.get(normalizeEmail(tm.invited_email)) : undefined);

        if (!matchedMember) continue;

        if (uiRole === "coach") coaches.push(matchedMember.id);
        if (uiRole === "joueur") players.push(matchedMember.id);
        if (uiRole === "parent") parents.push(matchedMember.id);
      }

      return {
        id: team.id,
        name: team.team_name || "Équipe",
        category: team.league || undefined,
        members: {
          coaches: uniq(coaches),
          players: uniq(players),
          parents: uniq(parents),
          parentLinks: [],
        },
      };
    });

    setMyRoles(Array.from(new Set(roles)));
    setMembers(clubMembers);
    setTeams(builtTeams);

    if (builtTeams.length > 0) {
      setSelectedTeamId((prev) =>
        prev && builtTeams.some((t) => t.id === prev) ? prev : builtTeams[0].id
      );
    } else {
      setSelectedTeamId("");
    }
  }, [clubId]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        setLoading(true);
        await loadPage();
      } catch (err: any) {
        if (!cancelled) {
          setErrorMsg(err?.message || "Impossible de charger les équipes du club.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [loadPage]);

  const coaches = useMemo(
    () =>
      members.filter(
        (m) =>
          m.roles.includes("coach") ||
          m.roles.includes("cadre_technique") ||
          m.roles.includes("dirigeant")
      ),
    [members]
  );

  const players = useMemo(
    () => members.filter((m) => m.roles.includes("joueur")),
    [members]
  );

  const parents = useMemo(
    () => members.filter((m) => m.roles.includes("parent")),
    [members]
  );

  const teamsFiltered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return teams;
    return teams.filter(
      (t) =>
        t.name.toLowerCase().includes(qq) ||
        (t.category ?? "").toLowerCase().includes(qq)
    );
  }, [teams, q]);

  async function addMember(teamId: string, role: TeamRole, clubMemberId: string) {
    if (!canManage || !clubMemberId) return;

    const member = members.find((m) => m.id === clubMemberId);
    if (!member) return;

    const dbRole = role === "joueur" ? "joueur" : role;

    setSaving(true);
    setErrorMsg(null);

    try {
      const payload: Record<string, unknown> = {
        team_id: teamId,
        role: dbRole,
        status: "active",
        invited_email: member.email !== "—" ? member.email : null,
        accepted_at: new Date().toISOString(),
      };

      if (member.userId) {
        payload.user_id = member.userId;
      }

      const { error } = await supabase.from("team_members").insert(payload);
      if (error) throw error;

      if (role === "coach") setPickCoach("");
      if (role === "joueur") setPickPlayer("");
      if (role === "parent") setPickParent("");

      await loadPage();
    } catch (err: any) {
      setErrorMsg(err?.message || "Impossible d’ajouter ce membre à l’équipe.");
    } finally {
      setSaving(false);
    }
  }

  async function removeMember(teamId: string, role: TeamRole, clubMemberId: string) {
    if (!canManage) return;

    const member = members.find((m) => m.id === clubMemberId);
    if (!member) return;

    const dbRole = role === "joueur" ? "joueur" : role;

    setSaving(true);
    setErrorMsg(null);

    try {
      let query = supabase
        .from("team_members")
        .delete()
        .eq("team_id", teamId)
        .eq("role", dbRole);

      if (member.userId) {
        query = query.eq("user_id", member.userId);
      } else if (member.email && member.email !== "—") {
        query = query.eq("invited_email", member.email);
      }

      const { error } = await query;
      if (error) throw error;

      await loadPage();
    } catch (err: any) {
      setErrorMsg(err?.message || "Impossible de retirer ce membre de l’équipe.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteTeam(teamId: string) {
    if (!canManage) return;

    const ok = window.confirm("Supprimer cette équipe ?");
    if (!ok) return;

    setSaving(true);
    setErrorMsg(null);

    try {
      const tmDelete = await supabase.from("team_members").delete().eq("team_id", teamId);
      if (tmDelete.error) throw tmDelete.error;

      const teamDelete = await supabase.from("teams").delete().eq("id", teamId);
      if (teamDelete.error) throw teamDelete.error;

      await loadPage();
    } catch (err: any) {
      setErrorMsg(err?.message || "Impossible de supprimer l’équipe.");
    } finally {
      setSaving(false);
    }
  }

  function memberName(id: string) {
    return members.find((m) => m.id === id)?.name ?? "Inconnu";
  }

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          Chargement des équipes…
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5" />
            <div>
              <div className="font-semibold">Erreur de chargement</div>
              <div className="text-sm mt-1">{errorMsg}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5" />
            <div>
              <div className="font-semibold">Accès refusé</div>
              <div className="text-sm mt-1">
                Cette page est réservée aux dirigeants, cadres techniques et coachs.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-center sm:justify-between sm:text-left">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Organisation</div>
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Équipes
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Assigne coachs, joueurs et parents à tes équipes.
          </div>
        </div>

        {canCreateTeam && (
          <Link
            to={`/club/${clubId}/teams/new`}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm"
          >
            <Plus className="h-4 w-4" />
            Nouvelle équipe
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-gray-500" />
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                Liste des équipes
              </div>
            </div>
            <div className="mt-3 relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher une équipe…"
                className="w-full pl-9 pr-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
              />
            </div>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {teamsFiltered.map((t) => {
              const active = t.id === selectedTeamId;

              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedTeamId(t.id)}
                  className={cx(
                    "w-full text-left p-4 transition",
                    active
                      ? "bg-gray-100 dark:bg-gray-800"
                      : "hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {t.name}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t.category ? `${t.category} • ` : ""}
                        {t.members.players.length} joueurs • {t.members.coaches.length} coach(s)
                      </div>
                    </div>
                    {t.category && <Pill>{t.category}</Pill>}
                  </div>
                </button>
              );
            })}

            {teamsFiltered.length === 0 && (
              <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
                Aucune équipe enregistrée pour l’instant.
              </div>
            )}
          </div>
        </div>

        <div className="xl:col-span-2 space-y-3">
          {!selectedTeam ? (
            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 text-sm text-gray-500 dark:text-gray-400">
              Sélectionne une équipe à gauche.
            </div>
          ) : (
            <>
              <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Équipe</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {selectedTeam.name}
                    </div>
                    <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {selectedTeam.category ? `Catégorie : ${selectedTeam.category} • ` : ""}
                      {selectedTeam.members.players.length} joueurs •{" "}
                      {selectedTeam.members.parents.length} parent(s) •{" "}
                      {selectedTeam.members.coaches.length} coach(s)
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/club/${clubId}/team/${selectedTeam.id}`}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Ouvrir l’équipe <ArrowRight className="h-4 w-4" />
                    </Link>

                    <button
                      type="button"
                      onClick={() => deleteTeam(selectedTeam.id)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 text-red-700 dark:bg-red-900/10 dark:text-red-200 text-sm hover:bg-red-100 dark:hover:bg-red-900/20"
                      disabled={saving}
                    >
                      <Trash2 className="h-4 w-4" />
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-gray-500" />
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    Ajouter des membres à l’équipe
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-3">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Coachs</div>
                    <div className="mt-2 flex gap-2">
                      <select
                        value={pickCoach}
                        onChange={(e) => setPickCoach(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100"
                        disabled={saving}
                      >
                        <option value="">Choisir…</option>
                        {coaches.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => addMember(selectedTeam.id, "coach", pickCoach)}
                        disabled={saving || !pickCoach}
                        className={cx(
                          "inline-flex items-center justify-center px-3 py-2 rounded-xl text-sm",
                          "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                          (saving || !pickCoach) && "opacity-60 cursor-not-allowed"
                        )}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="mt-3 space-y-2">
                      {selectedTeam.members.coaches.map((id) => (
                        <div
                          key={id}
                          className="flex items-center justify-between gap-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 px-3 py-2"
                        >
                          <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                            {memberName(id)}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeMember(selectedTeam.id, "coach", id)}
                            className="text-gray-500 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-300"
                            aria-label="Retirer coach"
                            disabled={saving}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {selectedTeam.members.coaches.length === 0 && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Aucun coach pour l’instant.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-3">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Joueurs</div>
                    <div className="mt-2 flex gap-2">
                      <select
                        value={pickPlayer}
                        onChange={(e) => setPickPlayer(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100"
                        disabled={saving}
                      >
                        <option value="">Choisir…</option>
                        {players.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => addMember(selectedTeam.id, "joueur", pickPlayer)}
                        disabled={saving || !pickPlayer}
                        className={cx(
                          "inline-flex items-center justify-center px-3 py-2 rounded-xl text-sm",
                          "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                          (saving || !pickPlayer) && "opacity-60 cursor-not-allowed"
                        )}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="mt-3 space-y-2">
                      {selectedTeam.members.players.map((id) => (
                        <div
                          key={id}
                          className="flex items-center justify-between gap-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 px-3 py-2"
                        >
                          <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                            {memberName(id)}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeMember(selectedTeam.id, "joueur", id)}
                            className="text-gray-500 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-300"
                            aria-label="Retirer joueur"
                            disabled={saving}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {selectedTeam.members.players.length === 0 && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Aucun joueur pour l’instant.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-3">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Parents</div>
                    <div className="mt-2 flex gap-2">
                      <select
                        value={pickParent}
                        onChange={(e) => setPickParent(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100"
                        disabled={saving}
                      >
                        <option value="">Choisir…</option>
                        {parents.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => addMember(selectedTeam.id, "parent", pickParent)}
                        disabled={saving || !pickParent}
                        className={cx(
                          "inline-flex items-center justify-center px-3 py-2 rounded-xl text-sm",
                          "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                          (saving || !pickParent) && "opacity-60 cursor-not-allowed"
                        )}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="mt-3 space-y-2">
                      {selectedTeam.members.parents.map((id) => (
                        <div
                          key={id}
                          className="flex items-center justify-between gap-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 px-3 py-2"
                        >
                          <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                            {memberName(id)}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeMember(selectedTeam.id, "parent", id)}
                            className="text-gray-500 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-300"
                            aria-label="Retirer parent"
                            disabled={saving}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {selectedTeam.members.parents.length === 0 && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Aucun parent pour l’instant.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500 dark:text-gray-400">
        Cette page est branchée sur <code>teams</code>, <code>team_members</code> et <code>club_members</code>.{" "}
        Pour lier un parent à son ou ses enfant(s), rends-toi sur la page membres de l’équipe.
      </div>
    </div>
  );
}